import { useState, useCallback, useMemo, memo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Alert,
  Badge,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Paper,
  Snackbar,
  Button,
  IconButton,
  Tooltip,
  LinearProgress,
  MenuItem,
  MenuList,
  Link,
} from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InfoIcon from '@mui/icons-material/Info';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import { UploadDocument } from './UploadDocuments';
import SelectInvoiceModal from './SelectInvoiceModal';
import DeductionInvoiceRef, { invoiceRefFrom, invoiceRefLabel } from './DeductionInvoiceRef';
import { Previewer } from '../../../../../components/DocumentPreviewer/Previewer';
import { getSignedUrl, getCompanyByPropertyId } from '../../api';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { format, parse } from 'date-fns';

const DEFAULT_DEDUCTION_OPTIONS = [
  { value: 'Cleaning Fee', label: 'Cleaning Fee' },
  { value: 'Damage Repair', label: 'Damage Repair' },
  { value: 'Unpaid Rent', label: 'Unpaid Rent' },
  { value: 'Owing Rent', label: 'Owing Rent' },
  { value: 'Key Replacement', label: 'Key Replacement' },
  { value: 'Late Fees', label: 'Late Fees' },
  { value: 'Non-Refundable Pet Fee', label: 'Non-Refundable Pet Fee' },
  { value: 'Painting of Walls', label: 'Painting of Walls' },
  { value: 'NSF Fee', label: 'NSF Fee (Returned Cheque)' },
  { value: 'Carpet Cleaning', label: 'Carpet Cleaning' },
  { value: 'Appliance Repair', label: 'Appliance Repair' },
  { value: 'Lock Change', label: 'Lock Change' },
  { value: 'Trash Removal', label: 'Trash Removal' },
  { value: 'Pest Control', label: 'Pest Control' },
  { value: 'Excessive Wear and Tear', label: 'Excessive Wear and Tear' },
];
// "Others" is pinned to the bottom of the description list. Choosing it hands the
// cell back to free typing instead of storing the literal word "Other".
const OTHER_DEDUCTION_OPTION = 'Other';
const OTHER_DEDUCTION_LABEL = 'Others (type manually)';
const isPresetDeduction = (value) => DEFAULT_DEDUCTION_OPTIONS.some((o) => o.value === value);

const formatAmount = (value) => {
  if (!value) return '$0.00';
  const numValue = parseFloat(value.toString().replace(/[$,]/g, '')) || 0;
  const absValue = Math.abs(numValue).toFixed(2);
  return numValue < 0 ? `-$${absValue}` : `$${absValue}`;
};

const SectionContent = memo(({ children, className = '' }) => (
  <Box className={`soa-section-content ${className}`} sx={{ padding: '16px' }}>
    {children}
  </Box>
));

const editableFieldStyles = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const getNestedError = (errors, touched, fieldName) => {
  const keys = fieldName.split(/[\.\[\]]/).filter(Boolean);
  let currentError = errors;
  let currentTouched = touched;

  for (const key of keys) {
    if (currentError == null) return null;
    if (Array.isArray(currentError)) {
      const index = parseInt(key, 10);
      if (isNaN(index) || index >= currentError.length) return null;
      currentError = currentError[index];
      currentTouched = currentTouched ? currentTouched[index] : undefined;
    } else {
      if (!(key in currentError)) return null;
      currentError = currentError[key];
      currentTouched = currentTouched ? currentTouched[key] : undefined;
    }
  }

  return typeof currentError === 'string' ? currentError : null;
};

const extractDateOnly = (dateValue) => {
  if (!dateValue) return '';

  const str = String(dateValue);

  // Case 1: Already just "YYYY-MM-DD"
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    return str;
  }

  // Case 2: Has T → extract the date part before T
  if (str.includes('T')) {
    const datePart = str.split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return datePart;
    }
  }

  // Case 3: Fallback — try to parse and format (still safe)
  try {
    const date = new Date(str);
    if (!isNaN(date)) {
      return date.toISOString().split('T')[0];
    }
  } catch (e) {
    // ignore
  }

  return '';
};

const EditableField = memo(
  ({
    name,
    value,
    type = 'text',
    className = '',
    placeholder = '',
    editingField,
    setEditingField,
    formik,
    disabled = false,
    onChange,
    sx = {},
  }) => {
    const isEditable = editingField === name && !disabled;
    const errorMessage = disabled ? null : getNestedError(formik.errors, formik.touched, name);
    const hasError = Boolean(errorMessage) && !disabled;

    const displayValue = useMemo(() => {
      if (value === null || value === undefined) return '';
      if (type === 'date' && value) {
        return extractDateOnly(value);
      }
      if (typeof value === 'object') {
        if (Array.isArray(value)) {
          return value
            .map((item) => (typeof item === 'object' && item !== null ? item.name || item.email || '' : item))
            .filter(Boolean)
            .join(', ');
        }
        return value.name || value.email || value.toString();
      }
      return value.toString();
    }, [value, type]);

    const [localValue, setLocalValue] = useState(type === 'date' && value ? extractDateOnly(value) : displayValue);

    useEffect(() => {
      if (!isEditable) {
        setLocalValue(type === 'date' && value ? extractDateOnly(value) : displayValue);
      }
    }, [displayValue, isEditable, type, value]);

    const handleFieldChange = (e) => {
      setLocalValue(e.target.value);
      if (onChange) onChange(e.target.value);
    };

    const handleBlur = useCallback(() => {
      if (name.includes('tenant') && onChange) {
        return;
      }
      let finalValue = localValue;
      if (type === 'date' && localValue) {
        finalValue = `${localValue}T00:00:00.000Z`;
      } else if (type === 'number') {
        finalValue = parseFloat(localValue) || 0;
      }
      formik.setFieldValue(name, finalValue);
      setEditingField(null);
    }, [formik, name, localValue, setEditingField, onChange, type]);

    const handleKeyPress = (e) => {
      if (e.key === 'Enter') handleBlur();
    };

    return (
      <Box className={`soa-editable-field ${className}`} sx={editableFieldStyles}>
        {isEditable ? (
          <Tooltip title={errorMessage || ''} placement="top-start" arrow>
            <TextField
              type={type}
              value={localValue || ''}
              onChange={handleFieldChange}
              onBlur={handleBlur}
              onKeyPress={handleKeyPress}
              placeholder={placeholder}
              autoFocus
              size="small"
              variant="standard"
              InputProps={{
                disableUnderline: true,
                ...(type === 'number' ? { startAdornment: <InputAdornment position="start">$</InputAdornment> } : {}),
              }}
              className="soa-edit-input"
              inputProps={{ 'aria-label': placeholder }}
              error={hasError}
              helperText={formik.touched && errorMessage ? errorMessage : ''}
              disabled={disabled}
              sx={{
                width: '100%',
                '& .MuiInput-root': {
                  color: disabled ? '#000000' : hasError ? 'error.main' : 'inherit',
                },
                ...sx,
              }}
            />
          </Tooltip>
        ) : (
          <Tooltip title={errorMessage || ''} placement="top-start" arrow>
            <Box
              className={`soa-display-value ${disabled ? 'disabled' : ''}`}
              onClick={() => !disabled && setEditingField(name)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && !disabled && setEditingField(name)}
              aria-label={`Edit ${placeholder}`}
              sx={{
                width: '100%',
                border: hasError ? '2px solid' : '2px solid transparent',
                borderColor: hasError ? 'error.main' : 'transparent',
                borderRadius: '4px',
                padding: hasError ? '4px 8px' : '6px 10px',
                '&:hover': {
                  backgroundColor: hasError ? 'error.light' : !disabled ? 'action.hover' : 'transparent',
                },
                color: disabled ? '#000000' : 'inherit',
                ...sx,
              }}
            >
              <Typography
                variant="body2"
                className="soa-field-text"
                sx={{
                  color: disabled ? '#000000' : hasError ? 'error.main' : 'inherit',
                  fontWeight: hasError ? 'medium' : 'normal',
                }}
              >
                {displayValue || (
                  <span className="soa-placeholder" style={{ color: disabled ? '#000000' : hasError ? 'inherit' : '#999' }}>
                    {placeholder}
                  </span>
                )}
              </Typography>
              {!disabled && (
                <FeatherIcon icon="edit" size={12} className="soa-edit-icon" style={{ color: disabled ? '#000000' : hasError ? 'inherit' : '#666' }} />
              )}
            </Box>
          </Tooltip>
        )}
      </Box>
    );
  }
);

const DeductionInput = ({
  index,
  field,
  type = 'text',
  placeholder,
  startAdornment,
  formik,
  onChange,
  disabled,
  inputRefs,
  rowIndex,
  fieldIndex,
  onTab,
  isFocused,
  setFocusedInput,
  deduction,
}) => {
  const inputRef = useRef(null);
  const displayRef = useRef(null);
  const fieldName = `account_details.deductions[${index}].${field}`;
  const errorMessage = disabled ? null : getNestedError(formik.errors, formik.touched, fieldName);
  const hasError = Boolean(errorMessage) && !disabled;

  const initialValue = useMemo(() => {
    const value = deduction?.[field];
    return value ?? (type === 'number' && field === 'number_of_actions' ? 0 : '');
  }, [deduction, field, type]);

  const [localValue, setLocalValue] = useState(initialValue);
  const [isEditing, setIsEditing] = useState(isFocused);
  const [showSelect, setShowSelect] = useState(false);
  const [inputMode, setInputMode] = useState('input'); // 'input' or 'select'

  const currentInputRef = isEditing ? inputRef : displayRef;

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(initialValue);
      setShowSelect(false);
      setInputMode('input');
    }
  }, [initialValue, isEditing]);

  // Description cell: the option list opens as soon as the cell enters edit mode
  // (first click, Enter, or Tab), so the choices show before any typing.
  useEffect(() => {
    if (isEditing && field === 'name' && !disabled) {
      setShowSelect(true);
      setInputMode('select');
    }
  }, [isEditing, field, disabled]);

  useEffect(() => {
    if (inputRefs.current) {
      if (!inputRefs.current[rowIndex]) {
        inputRefs.current[rowIndex] = [];
      }
      if (currentInputRef.current) {
        inputRefs.current[rowIndex][fieldIndex] = currentInputRef.current;
      }
    }
  }, [inputRefs, rowIndex, fieldIndex, currentInputRef]);

  useEffect(() => {
    if (isFocused && !disabled) {
      if (!isEditing) {
        setIsEditing(true);
        setInputMode('input');
        setShowSelect(false);
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
            if (field !== 'name' && typeof inputRef.current.select === 'function') {
              inputRef.current.select();
            }
          }
        }, 0);
      } else {
        if (inputRef.current) {
          inputRef.current.focus();
          if (field !== 'name' && typeof inputRef.current.select === 'function') {
            inputRef.current.select();
          }
        }
      }
    }
  }, [isFocused, disabled, isEditing, field]);

  const handleChange = useCallback((value) => {
    setLocalValue(value);
    onChange(index, field, value);
  }, [onChange, index, field]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setLocalValue(value);
    onChange(index, field, value);
  };

  const handleBlur = useCallback((e) => {
    const relatedTarget = e.relatedTarget;
    if (relatedTarget && relatedTarget.closest('[role="menuitem"]')) {
      return;
    }

    setIsEditing(false);
    setShowSelect(false);
    setInputMode('input');
    setFocusedInput({ rowIndex: null, fieldIndex: null });
  }, [setFocusedInput]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      if (field === 'name' && inputMode === 'input' && !showSelect) {
        setShowSelect(true);
        setInputMode('select');
      } else {
        setIsEditing(false);
        setShowSelect(false);
        setInputMode('input');
        setFocusedInput({ rowIndex: null, fieldIndex: null });
      }
    }
  }, [field, inputMode, showSelect, setFocusedInput]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Tab' && onTab) {
      e.preventDefault();
      setIsEditing(false);
      setShowSelect(false);
      setInputMode('input');
      setFocusedInput({ rowIndex: null, fieldIndex: null });
      onTab(rowIndex, fieldIndex, e.shiftKey);
    } else if (e.key === 'Escape') {
      setShowSelect(false);
      setInputMode('input');
    }
  }, [onTab, rowIndex, fieldIndex, setFocusedInput]);

  const handleDisplayKeyDown = useCallback((e) => {
    if (e.key === 'Tab' && onTab) {
      e.preventDefault();
      onTab(rowIndex, fieldIndex, e.shiftKey);
    } else if (e.key === 'Enter' && !disabled && !isEditing) {
      setIsEditing(true);
      setInputMode('input');
      setShowSelect(false);
      setFocusedInput({ rowIndex, fieldIndex });
    }
  }, [onTab, rowIndex, fieldIndex, disabled, isEditing, setFocusedInput]);

  const displayValue = useMemo(() => {
    if (type === 'number' && field === 'cost_per_action') {
      return formatAmount(localValue);
    }
    return localValue || '';
  }, [localValue, type, field]);

  const handleClick = useCallback(() => {
    if (!disabled && !isEditing) {
      setIsEditing(true);
      setInputMode('input');
      setShowSelect(false);
      setFocusedInput({ rowIndex, fieldIndex });
    }
  }, [disabled, isEditing, setFocusedInput, rowIndex, fieldIndex]);

  const handleInputClick = useCallback(() => {
    if (field === 'name' && inputMode === 'input' && !showSelect) {
      setShowSelect(true);
      setInputMode('select');
    }
  }, [field, inputMode, showSelect]);

  const handleOptionClick = useCallback((optionValue) => {
    // "Others": clear a preset so the user can type their own description,
    // but keep an existing custom text so it can be edited.
    const nextValue = optionValue === OTHER_DEDUCTION_OPTION
      ? (isPresetDeduction(localValue) ? '' : localValue)
      : optionValue;
    setLocalValue(nextValue);
    onChange(index, field, nextValue);
    setShowSelect(false);
    setInputMode('input');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [onChange, index, field, localValue]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}>
      {isEditing ? (
        <Tooltip title={errorMessage || ''} placement="top-start" arrow>
          <Box sx={{ position: 'relative', width: '100%' }}>
            <TextField
              inputRef={inputRef}
              type={type}
              value={localValue}
              onChange={handleInputChange}
              onBlur={handleBlur}
              onKeyPress={handleKeyPress}
              onKeyDown={handleKeyDown}
              onClick={handleInputClick}
              placeholder={field === 'name' ? `${placeholder} (Enter or click for options)` : placeholder}
              size="small"
              variant="standard"
              className="soa-deduction-input"
              inputProps={{
                'aria-label': placeholder,
                min: type === 'number' ? 0 : undefined,
                step: type === 'number' && field === 'cost_per_action' ? '0.01' : undefined,
                style: { fontSize: '0.9rem' },
              }}
              InputProps={{
                ...(startAdornment ? { startAdornment } : {}),
                style: { fontSize: '0.9rem' },
              }}
              disabled={disabled}
              error={hasError}
              sx={{
                width: '100%',
                '& .MuiInput-root': {
                  color: disabled ? 'inherit' : hasError ? 'error.main' : 'inherit',
                },
                '& .MuiInput-underline:before': {
                  borderBottomColor: disabled ? 'rgba(0, 0, 0, 0.42)' : hasError ? 'error.main' : 'rgba(0, 0, 0, 0.42)',
                },
                '& .MuiInput-underline:after': {
                  borderBottomColor: disabled ? 'primary.main' : hasError ? 'error.main' : 'primary.main',
                },
              }}
            />

            {/* Dropdown Options */}
            {field === 'name' && showSelect && createPortal(
              <Paper
                sx={{
                  position: 'absolute',
                  top: inputRef.current?.getBoundingClientRect().bottom + window.scrollY,
                  left: inputRef.current?.getBoundingClientRect().left + window.scrollX,
                  zIndex: 9999,
                  maxHeight: 200,
                  overflowY: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 3,
                }}
              >
                <MenuList dense>
                  {DEFAULT_DEDUCTION_OPTIONS.map((option) => (
                    <MenuItem
                      key={option.value}
                      onClick={() => handleOptionClick(option.value)}
                      selected={localValue === option.value}
                      role="menuitem"
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                  <MenuItem
                    onClick={() => handleOptionClick(OTHER_DEDUCTION_OPTION)}
                    selected={Boolean(localValue) && !isPresetDeduction(localValue)}
                    role="menuitem"
                    sx={{ borderTop: '1px solid', borderColor: 'divider', fontStyle: 'italic' }}
                  >
                    {OTHER_DEDUCTION_LABEL}
                  </MenuItem>
                </MenuList>
              </Paper>,
              document.body
            )}
          </Box>
        </Tooltip>
      ) : (
        <Tooltip title={errorMessage || ''} placement="top-start" arrow>
          <Box
            ref={displayRef}
            className={`soa-display-value ${disabled ? 'disabled' : ''}`}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={handleDisplayKeyDown}
            aria-label={`Edit ${placeholder}`}
            sx={{
              width: '100%',
              padding: hasError ? '4px 8px' : '6px 10px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              border: hasError ? '2px solid' : '2px solid transparent',
              borderColor: hasError ? 'error.main' : 'transparent',
              borderRadius: '4px',
              cursor: disabled ? 'default' : 'pointer',
              '&:hover': {
                backgroundColor: hasError ? 'error.light' : !disabled ? 'action.hover' : 'transparent',
              },
              transition: 'all 0.2s ease-in-out',
              color: disabled ? 'inherit' : 'inherit',
            }}
          >
            <Typography
              variant="body2"
              className="soa-field-text"
              sx={{
                color: disabled ? 'inherit' : hasError ? 'error.main' : 'inherit',
                fontWeight: hasError ? 'medium' : 'normal',
                fontSize: '0.9rem',
                flexGrow: 1,
              }}
            >
              {displayValue || (
                <span className="soa-placeholder" style={{ color: disabled ? '#999' : hasError ? 'inherit' : '#999', fontStyle: 'italic' }}>
                  {placeholder}
                </span>
              )}
            </Typography>
            {!disabled && (
              <FeatherIcon icon="edit" size={12} className="soa-edit-icon" style={{ color: disabled ? '#666' : hasError ? 'inherit' : '#666', opacity: 0.7 }} />
            )}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

const DeductionsTable = ({ formik, isEditable }) => {
  const inputRefs = useRef([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedDeductionIndex, setSelectedDeductionIndex] = useState(null);
  const [selectedInvoiceUrl, setSelectedInvoiceUrl] = useState('');
  const [selectedFileType, setSelectedFileType] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [focusedInput, setFocusedInput] = useState({ rowIndex: null, fieldIndex: null });
  const [localDeductions, setLocalDeductions] = useState(() => {
    const deductions = formik.values.account_details?.deductions || [];
    if (deductions.length > 0) {
      return deductions;
    }
    if (!formik.values.account_details) {
      return [{ name: '', cost_per_action: 0, number_of_actions: 0, invoice: { url: '', description: '' }, invoice_ref: null }];
    }
    return [];
  });
  const [invoicePickerIndex, setInvoicePickerIndex] = useState(null);

  const ensureAccountDetailsExist = useCallback(() => {
    if (!formik.values.account_details) {
      formik.setFieldValue('account_details', {});
    }
  }, [formik]);

  useEffect(() => {
    ensureAccountDetailsExist();
  }, [ensureAccountDetailsExist]);

  useEffect(() => {
    if (focusedInput.rowIndex === null && focusedInput.fieldIndex === null) {
      const currentFormikDeductions = formik.values.account_details?.deductions || [];
      const areDeductionsEqual = JSON.stringify(currentFormikDeductions) === JSON.stringify(localDeductions);
      if (!areDeductionsEqual) {
        formik.setFieldValue('account_details.deductions', localDeductions);
        try {
          formik.validateField('account_details.deductions');
        } catch (error) {
          console.warn('Validation error for deductions:', error);
        }
      }
    }
  }, [focusedInput, localDeductions, formik]);

  useEffect(() => {
    const deductionsCount = Math.max(localDeductions.length, 0);
    inputRefs.current = Array.from({ length: deductionsCount }, (_, rowIndex) => inputRefs.current[rowIndex] || Array(3).fill(null));
  }, [localDeductions.length]);

  const totalDeductions = useMemo(() => {
    return localDeductions.reduce(
      (sum, deduction) =>
        sum + (Number(deduction.cost_per_action) || 0) * (Number(deduction.number_of_actions) || 0),
      0
    );
  }, [localDeductions]);

  const balance = useMemo(() => {
    const totalSecurity = Number(formik.values.account_details?.security) || 0;
    const totalInterest = Number(formik.values.account_details?.interest) || 0;
    const earlyMoveOutProrate = Number(formik.values.account_details?.early_move_out_prorate) || 0;
    return totalSecurity + totalInterest + earlyMoveOutProrate - totalDeductions;
  }, [
    formik.values.account_details?.security,
    formik.values.account_details?.interest,
    formik.values.account_details?.early_move_out_prorate,
    totalDeductions
  ]);

  const handleChange = useCallback((index, field, value) => {
    setLocalDeductions((prev) => {
      const newDeductions = [...prev];
      if (index >= newDeductions.length) {
        while (newDeductions.length <= index) {
          newDeductions.push({
            name: '',
            cost_per_action: 0,
            number_of_actions: 0,
            invoice: { url: '', description: '' },
            invoice_ref: null,
          });
        }
      }
      newDeductions[index] = {
        ...newDeductions[index],
        [field]: field === 'cost_per_action' || field === 'number_of_actions' ? (value ? Number(value) : 0) : value,
      };
      return newDeductions;
    });
  }, []);

  const addDeduction = useCallback(() => {
    const newDeduction = {
      name: '',
      cost_per_action: 0,
      number_of_actions: 0,
      invoice: { url: '', description: '' },
      invoice_ref: null,
    };
    setLocalDeductions((prev) => {
      const updated = [...prev, newDeduction];
      setTimeout(() => {
        const newRowIndex = updated.length - 1;
        setFocusedInput({ rowIndex: newRowIndex, fieldIndex: 0 });
      }, 100);
      return updated;
    });
  }, []);

  const removeDeduction = useCallback(
    (index) => {
      setLocalDeductions((prev) => {
        const updated = prev.filter((_, i) => i !== index);
        if (inputRefs.current[index]) {
          inputRefs.current.splice(index, 1);
        }
        if (focusedInput.rowIndex === index) {
          setFocusedInput({ rowIndex: null, fieldIndex: null });
        } else if (focusedInput.rowIndex > index) {
          setFocusedInput({
            rowIndex: focusedInput.rowIndex - 1,
            fieldIndex: focusedInput.fieldIndex,
          });
        }
        return updated;
      });
    },
    [focusedInput]
  );

  const handleTabNavigation = useCallback(
    (currentRowIndex, currentFieldIndex, isShiftKey) => {
      const totalFields = 3;
      const currentDeductionsCount = localDeductions.length;

      if (currentDeductionsCount === 0) return;

      let nextRowIndex = currentRowIndex;
      let nextFieldIndex = currentFieldIndex;

      if (!isShiftKey) {
        nextFieldIndex += 1;
        if (nextFieldIndex >= totalFields) {
          nextFieldIndex = 0;
          nextRowIndex += 1;
          if (nextRowIndex >= currentDeductionsCount) {
            nextRowIndex = 0;
          }
        }
      } else {
        nextFieldIndex -= 1;
        if (nextFieldIndex < 0) {
          nextFieldIndex = totalFields - 1;
          nextRowIndex -= 1;
          if (nextRowIndex < 0) {
            nextRowIndex = currentDeductionsCount - 1;
          }
        }
      }

      setFocusedInput({ rowIndex: nextRowIndex, fieldIndex: nextFieldIndex });
    },
    [localDeductions.length]
  );

  const extractS3KeyAndType = (invoice) => {
    if (!invoice || (!invoice.url && typeof invoice !== 'string')) {
      return { key: '', fileType: '' };
    }
    let url = typeof invoice === 'string' ? invoice : invoice.url || '';
    if (!url) {
      return { key: '', fileType: '' };
    }
    const urlPattern = /^https:\/\/[^/]+\/(.+)$/;
    const match = url.match(urlPattern);
    const key = match ? match[1] : url;
    const fileType = key.split('.').pop()?.toLowerCase() || (typeof invoice === 'object' && invoice.fileType?.toLowerCase()) || '';
    return { key, fileType };
  };

  const handleUploadInvoice = useCallback(
    async (index) => {
      let deduction = localDeductions[index];
      if (!deduction) {
        handleChange(index, 'name', '');
        await new Promise((resolve) => setTimeout(resolve, 0));
        deduction = localDeductions[index];
      }
      if (!deduction) {
        setSnackbar({ open: true, message: 'Invalid deduction selected.' });
        return;
      }
      const hasExistingInvoice = deduction?.invoice?.url && deduction.invoice.url.trim() !== '';
      if (hasExistingInvoice) {
        try {
          const { key, fileType } = extractS3KeyAndType(deduction.invoice);
          if (!key) {
            throw new Error('Invalid invoice key');
          }
          const response = await getSignedUrl(key, 'get', fileType);
          if (response.success && response.data.signedUrl) {
            setSelectedInvoiceUrl(response.data.signedUrl);
            setSelectedFileType(fileType);
            setSelectedDeductionIndex(index);
            setPreviewDialogOpen(true);
          } else {
            setSnackbar({ open: true, message: 'Failed to load the document for preview.' });
          }
        } catch (error) {
          console.error('Error fetching signed URL:', error);
          setSnackbar({ open: true, message: `An error occurred while loading the document: ${error.message}` });
        }
      } else {
        setSelectedDeductionIndex(index);
        setUploadDialogOpen(true);
      }
    },
    [localDeductions, handleChange]
  );

  const handleDownloadInvoice = useCallback(async () => {
    try {
      const { url, fileType } = { url: selectedInvoiceUrl, fileType: selectedFileType };
      if (!url) {
        setSnackbar({ open: true, message: 'No invoice available to download.' });
        return;
      }
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch invoice');
      const blob = await response.blob();
      const fileName = `invoice.${fileType || 'pdf'}`;
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      setSnackbar({ open: true, message: 'Failed to download the invoice. Please try again.' });
    }
  }, [selectedInvoiceUrl, selectedFileType]);

  const handleUploadClose = useCallback(() => {
    setUploadDialogOpen(false);
    setSelectedDeductionIndex(null);
  }, []);

  const handlePreviewClose = useCallback(() => {
    setPreviewDialogOpen(false);
    setSelectedInvoiceUrl('');
    setSelectedFileType('');
    setSelectedDeductionIndex(null);
  }, []);

  const getInvoiceFieldName = useCallback((index) => {
    if (index === null || index === undefined) return '';
    return `account_details.deductions[${index}].invoice`;
  }, []);

  // Link a deduction to the approved invoice that paid for the work. The picker
  // only offers invoices for this SOA's property / building / unit.
  const handleInvoicePicked = useCallback(
    (inv) => {
      const index = invoicePickerIndex;
      setInvoicePickerIndex(null);
      if (index === null || index === undefined) return;
      const ref = invoiceRefFrom(inv);
      setLocalDeductions((prev) => {
        const next = [...prev];
        const current = next[index] || { name: '', cost_per_action: 0, number_of_actions: 0, invoice: { url: '', description: '' } };
        // Only the reference is stored; the description stays the manager's own
        // words, since it is the one part of the row the tenant will read.
        next[index] = { ...current, invoice_ref: ref };
        return next;
      });
      setFocusedInput({ rowIndex: null, fieldIndex: null });
    },
    [invoicePickerIndex]
  );

  // Attaching is optional: in-house work carries no invoice, so a linked row can
  // be unlinked again without leaving the deduction invalid.
  const clearInvoiceLink = useCallback((index) => {
    setLocalDeductions((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      next[index] = { ...next[index], invoice_ref: null };
      return next;
    });
  }, []);

  const displayDeductions = localDeductions;
  const hasNoDeductions = displayDeductions.length === 0;

  return (
    <Box className="soa-deductions-container" sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
      <Typography variant="h6" className="soa-section-title" sx={{ mb: 2, fontWeight: 'medium' }}>
        Less Deductions, as Listed:
      </Typography>
      <Paper variant="outlined" className="soa-deductions-table" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell className="soa-table-header" sx={{ width: '5%', fontWeight: 'bold' }}>No.</TableCell>
              <TableCell className="soa-table-header" sx={{ width: '25%', fontWeight: 'bold' }}>Description</TableCell>
              <TableCell className="soa-table-header" sx={{ width: '15%', fontWeight: 'bold' }}>Cost Per Action</TableCell>
              <TableCell className="soa-table-header" sx={{ width: '10%', fontWeight: 'bold' }}>Quantity</TableCell>
              <TableCell className="soa-table-header" sx={{ width: '10%', fontWeight: 'bold' }}>Total</TableCell>
              <TableCell className="soa-table-header" sx={{ width: '15%', fontWeight: 'bold' }}>Invoice</TableCell>
              <TableCell className="soa-table-header" sx={{ width: '10%', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {hasNoDeductions ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, fontStyle: 'italic', color: 'text.secondary' }}>
                  No deductions listed
                  {isEditable && (
                    <Box sx={{ mt: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={addDeduction}
                        startIcon={<FeatherIcon icon="plus" size={16} />}
                      >
                        Add Deduction
                      </Button>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              displayDeductions.map((deduction, index) => {
                const rowError = formik.errors.account_details?.deductions?.[index];
                const hasRowError = !isEditable && rowError && typeof rowError === 'object' && Object.keys(rowError).length > 0;
                return (
                  <TableRow
                    key={`deduction-${index}`}
                    className="soa-deduction-row"
                    sx={{ '&:hover': isEditable ? { bgcolor: 'action.hover' } : {}, transition: 'background-color 0.2s ease-in-out' }}
                  >
                    <TableCell className="soa-table-cell" sx={{ width: '5%' }}>{index + 1}</TableCell>
                    <TableCell className="soa-table-cell" sx={{ width: '25%' }}>
                      <DeductionInput
                        index={index}
                        field="name"
                        placeholder="Select or enter description"
                        formik={formik}
                        onChange={handleChange}
                        disabled={!isEditable}
                        inputRefs={inputRefs}
                        rowIndex={index}
                        fieldIndex={0}
                        onTab={handleTabNavigation}
                        isFocused={focusedInput.rowIndex === index && focusedInput.fieldIndex === 0}
                        setFocusedInput={setFocusedInput}
                        deduction={deduction}
                      />
                    </TableCell>
                    <TableCell className="soa-table-cell" sx={{ width: '15%' }}>
                      <DeductionInput
                        index={index}
                        field="cost_per_action"
                        type="number"
                        placeholder="0.00"
                        startAdornment={<InputAdornment position="start">$</InputAdornment>}
                        formik={formik}
                        onChange={handleChange}
                        disabled={!isEditable}
                        inputRefs={inputRefs}
                        rowIndex={index}
                        fieldIndex={1}
                        onTab={handleTabNavigation}
                        isFocused={focusedInput.rowIndex === index && focusedInput.fieldIndex === 1}
                        setFocusedInput={setFocusedInput}
                        deduction={deduction}
                      />
                    </TableCell>
                    <TableCell className="soa-table-cell" sx={{ width: '10%' }}>
                      <DeductionInput
                        index={index}
                        field="number_of_actions"
                        type="number"
                        placeholder="0"
                        formik={formik}
                        onChange={handleChange}
                        disabled={!isEditable}
                        inputRefs={inputRefs}
                        rowIndex={index}
                        fieldIndex={2}
                        onTab={handleTabNavigation}
                        isFocused={focusedInput.rowIndex === index && focusedInput.fieldIndex === 2}
                        setFocusedInput={setFocusedInput}
                        deduction={deduction}
                      />
                    </TableCell>
                    <TableCell className="soa-table-cell soa-calculated-total" sx={{ width: '10%', color: hasRowError ? 'error.main' : 'inherit' }}>
                      {formatAmount((Number(deduction.cost_per_action) || 0) * (Number(deduction.number_of_actions) || 0))}
                    </TableCell>
                    <TableCell className="soa-table-cell soa-invoice-cell" sx={{ width: '15%' }}>
                      {deduction.invoice_ref?.invoice_id ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DeductionInvoiceRef invoiceRef={deduction.invoice_ref} />
                          {isEditable && (
                            <Tooltip title={`Change invoice (currently ${invoiceRefLabel(deduction.invoice_ref)})`}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setInvoicePickerIndex(index);
                                }}
                                aria-label="Change linked invoice"
                              >
                                <FeatherIcon icon="edit-2" size={14} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isEditable && (
                            <Tooltip title="Remove the linked invoice (in-house work needs none)">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  clearInvoiceLink(index);
                                }}
                                aria-label="Remove linked invoice"
                              >
                                <FeatherIcon icon="x" size={14} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      ) : deduction.invoice?.url && deduction.invoice.url.trim() !== '' ? (
                        <Tooltip title={deduction.invoice.description ? `View invoice: ${deduction.invoice.description}` : 'View invoice'}>
                          <Badge badgeContent={<span className="soa-invoice-badge">1</span>} overlap="circular" anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUploadInvoice(index);
                              }}
                              aria-label="View uploaded invoice"
                              sx={{
                                bgcolor: 'primary.light',
                                '&:hover': { bgcolor: 'primary.main', color: 'white', transform: 'scale(1.1)' },
                                transition: 'all 0.2s ease-in-out',
                                borderRadius: '8px',
                              }}
                            >
                              <AttachFileIcon fontSize="small" />
                            </IconButton>
                          </Badge>
                        </Tooltip>
                      ) : isEditable ? (
                        <Tooltip title="Attach the approved invoice for this work. Leave it off for in-house work.">
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<AttachFileIcon fontSize="small" />}
                            onClick={(e) => {
                              e.stopPropagation();
                              setInvoicePickerIndex(index);
                            }}
                            aria-label="Attach invoice (optional)"
                            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                          >
                            Attach invoice
                          </Button>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">No Invoice</Typography>
                      )}
                    </TableCell>
                    <TableCell className="soa-table-cell" sx={{ width: '10%' }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Remove deduction">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeDeduction(index);
                            }}
                            aria-label="Remove deduction"
                            disabled={!isEditable}
                          >
                            <FeatherIcon icon="minus" size={16} />
                          </IconButton>
                        </Tooltip>
                        {index === localDeductions.length - 1 && isEditable && (
                          <Tooltip title="Add deduction">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                addDeduction();
                              }}
                              aria-label="Add deduction"
                            >
                              <FeatherIcon icon="plus" size={16} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Paper>

      {hasNoDeductions && !isEditable && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No deductions were applied to this security deposit
          </Typography>
        </Box>
      )}

      <Box className="soa-totals-summary">
        <Typography variant="body1" className="soa-total-deductions">
          Total Deductions: {formatAmount(totalDeductions)}
        </Typography>
        <Typography variant="body1" className={`soa-total-balance ${balance >= 0 ? 'positive' : 'negative'}`}>
          {balance >= 0
            ? `Please return ${formatAmount(Math.abs(balance))} to the Tenant`
            : `Please collect ${formatAmount(Math.abs(balance))} from the Tenant for the Landlord`}
        </Typography>
      </Box>

      <UploadDocument
        isOpen={uploadDialogOpen}
        onClose={handleUploadClose}
        formik={formik}
        urlFieldName={selectedDeductionIndex !== null ? getInvoiceFieldName(selectedDeductionIndex) : ''}
      />

      <SelectInvoiceModal
        isOpen={invoicePickerIndex !== null}
        onClose={() => setInvoicePickerIndex(null)}
        propertyDetails={formik.values.property_details}
        soaId={formik.values._id || formik.values.id || ''}
        currentInvoiceId={invoicePickerIndex !== null ? localDeductions[invoicePickerIndex]?.invoice_ref?.invoice_id : null}
        onSelect={handleInvoicePicked}
      />

      <Dialog open={previewDialogOpen} onClose={handlePreviewClose} maxWidth="md" fullWidth aria-labelledby="preview-invoice-dialog-title">
        <DialogTitle id="preview-invoice-dialog-title">Invoice Preview</DialogTitle>
        <DialogContent>
          {localDeductions?.[selectedDeductionIndex]?.invoice?.description && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Description:</Typography>
              <Typography variant="body2">{localDeductions?.[selectedDeductionIndex]?.invoice?.description}</Typography>
            </Box>
          )}
          <Previewer signedUrl={selectedInvoiceUrl} type={selectedFileType} height="70vh" width="100%" />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" startIcon={<DownloadIcon />} onClick={handleDownloadInvoice} aria-label="Download invoice">
            Download Invoice
          </Button>
          <Button onClick={handlePreviewClose} color="primary" aria-label="Close preview">Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ open: false, message: '' })}>
        <Alert severity="error">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

const SOAForm = ({ formik, isEditable = true }) => {
  const [companyDetails, setCompanyDetails] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [showProgress, setShowProgress] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const soaFormRef = useRef(null);

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        const propertyId = formik.values.property_details?.property_id;
        const response = await getCompanyByPropertyId(propertyId);
        setCompanyDetails(response);
      } catch (error) {
        console.error('Failed to fetch company details:', error);
      }
    };
    if (formik.values.property_details?.property_id) {
      fetchCompanyDetails();
    }
  }, [formik.values.property_details?.property_id]);

  const isValidEmail = (email) => {
    if (!email || typeof email !== 'string') return false;
    const emailLower = email.trim().toLowerCase();
    if (emailLower === 'n/a') return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailLower);
  };

  useEffect(() => {
    const tenants = formik.values.tenant || [{ name: '', email: '' }];
    if (!formik.values.email_details) {
      formik.setFieldValue('email_details', {
        name: '',
        email: '',
        cc: [],
        attachments: [],
        notes: '',
      });
      return;
    }
    const firstTenant = tenants[0] || { name: '', email: '' };
    const newEmailDetails = {
      ...formik.values.email_details,
      name: firstTenant.name || '',
      email: isValidEmail(firstTenant.email) ? firstTenant.email : '',
      cc: tenants.slice(1).map((tenant) => tenant.email).filter((email) => isValidEmail(email)),
    };
    if (JSON.stringify(newEmailDetails) !== JSON.stringify(formik.values.email_details)) {
      formik.setFieldValue('email_details', newEmailDetails);
    }
  }, [formik.values.tenant, formik]);

  const handleTenantChange = useCallback(
    (index, field, value) => {
      const updatedTenants = [...(formik.values.tenant || [{ name: '', email: '' }])];
      updatedTenants[index] = { ...updatedTenants[index], [field]: value };
      formik.setFieldValue('tenant', updatedTenants);
    },
    [formik]
  );

  const addTenant = useCallback(() => {
    const updatedTenants = [...(formik.values.tenant || [{ name: '', email: '' }]), { name: '', email: 'N/A' }];
    formik.setFieldValue('tenant', updatedTenants);
  }, [formik]);

  const removeTenant = useCallback(
    (index) => {
      const updatedTenants = (formik.values.tenant || []).filter((_, i) => i !== index);
      formik.setFieldValue('tenant', updatedTenants.length ? updatedTenants : [{ name: '', email: '' }]);
    },
    [formik]
  );

  const setTenantEmailNA = useCallback(
    (index) => {
      if (index === 0) return;
      handleTenantChange(index, 'email', 'N/A');
      if (editingField === `tenant[${index}].email`) {
        setEditingField(null);
      }
    },
    [handleTenantChange, editingField, setEditingField]
  );

  const generatePDFFile = useCallback(async (showProgressUpdates = false) => {
    if (!soaFormRef.current) return null;
    try {
      if (showProgressUpdates) {
        setShowProgress(true);
        setProgressMessage('Preparing document...');
        setProgress(5);
      }
      const pdf = new jsPDF('p', 'mm', 'a4');
      const soaElement = soaFormRef.current;
      if (showProgressUpdates) {
        setProgressMessage('Capturing document...');
        setProgress(25);
      }
      const canvas = await html2canvas(soaElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: '#ffffff',
        removeContainer: true,
        imageTimeout: 15000,
        height: soaElement.scrollHeight,
        width: soaElement.scrollWidth,
      });
      if (showProgressUpdates) {
        setProgressMessage('Processing image...');
        setProgress(50);
      }
      const imgData = canvas.toDataURL('image/jpeg', 0.9);
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const sidePadding = 10;
      const topBottomPadding = 5;
      const availableWidth = pdfWidth - sidePadding * 2;
      const availableHeight = pdfHeight - topBottomPadding * 2;
      const imgWidth = imgProps.width;
      const imgHeight = imgProps.height;
      const ratio = Math.min(availableWidth / imgWidth, availableHeight / imgHeight);
      const scaledWidth = imgWidth * ratio;
      const scaledHeight = imgHeight * ratio;
      const xOffset = sidePadding + (availableWidth - scaledWidth) / 2;
      const yOffset = topBottomPadding;
      if (showProgressUpdates) {
        setProgressMessage('Adding to PDF...');
        setProgress(75);
      }
      const pageHeight = availableHeight;
      const totalPages = Math.ceil(scaledHeight / pageHeight);
      for (let i = 0; i < totalPages; i++) {
        if (i > 0) {
          pdf.addPage();
        }
        const sourceY = i * (pageHeight / ratio);
        const sourceHeight = Math.min(pageHeight / ratio, imgHeight - sourceY);
        if (sourceHeight > 0) {
          const pageCanvas = document.createElement('canvas');
          const pageCtx = pageCanvas.getContext('2d');
          const img = new Image();
          await new Promise((resolve) => {
            img.onload = () => {
              pageCanvas.width = imgWidth;
              pageCanvas.height = sourceHeight;
              pageCtx.drawImage(img, 0, -sourceY);
              const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.9);
              pdf.addImage(pageImgData, 'JPEG', xOffset, yOffset, scaledWidth, sourceHeight * ratio);
              resolve();
            };
            img.src = imgData;
          });
        }
      }
      if (showProgressUpdates) {
        setProgressMessage('Finalizing PDF...');
        setProgress(90);
      }
      const pdfBlob = pdf.output('blob');
      const pdfFile = new File([pdfBlob], 'SOA-Statement.pdf', { type: 'application/pdf' });
      if (showProgressUpdates) {
        setProgress(100);
        setProgressMessage('PDF generated successfully!');
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
      return pdfFile;
    } catch (error) {
      console.error('Error generating PDF:', error);
      if (showProgressUpdates) {
        setProgressMessage('Error generating PDF.');
      }
      throw error;
    } finally {
      if (showProgressUpdates) {
        setTimeout(() => {
          setShowProgress(false);
          setProgress(0);
          setProgressMessage('');
        }, 1000);
      }
    }
  }, []);

  const handleDownloadPDF = useCallback(async () => {
    try {
      const pdfFile = await generatePDFFile(true);
      if (pdfFile) {
        const url = URL.createObjectURL(pdfFile);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'SOA-Statement.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Download failed:', error);
      formik.setFieldValue('error', 'Failed to generate PDF for download. Please try again.');
    }
  }, [generatePDFFile, formik]);

  const tenants = formik.values.tenant || [{ name: '', email: '' }];

  const formatTenantsDisplay = useCallback(() => {
    return tenants
      .map((tenant) => {
        const name = tenant.name || '';
        const email = tenant.email || '';
        return email ? `${name} (${email})` : name;
      })
      .filter((item) => item.trim())
      .join(', ') || 'No tenant information provided';
  }, [tenants]);

  const hasTenantError = useMemo(() => {
    if (!isEditable) return false;
    const tenantErrors = formik.errors.tenant;
    if (!tenantErrors || !Array.isArray(tenantErrors)) return false;
    return tenantErrors.some((errorObj) => errorObj && typeof errorObj === 'object' && Object.values(errorObj).some((err) => typeof err === 'string' && err.length > 0));
  }, [formik.errors.tenant, isEditable]);

  const SecurityDepositSummary = memo(() => (
    <Box className="soa-deposit-container">
      <Typography variant="h6" className="soa-section-title text-center">
        Security Deposit Summary
      </Typography>
      <Paper variant="outlined" className="soa-deposit-summary">
        <Box className="card-content">
          <Box className="card-header" display="flex" flexDirection="column" sx={{ mb: "0px !important" }}>
            <Box display="flex" alignItems="center">
              <Typography variant="body1" className="total-deposit">
                Total Security Deposit Received:
              </Typography>
              <EditableField
                name="account_details.security"
                value={formatAmount(formik.values.account_details?.security)}
                type="number"
                placeholder="0.00"
                editingField={editingField}
                setEditingField={setEditingField}
                formik={formik}
                className="soa-deposit-amount"
                disabled={!isEditable}
                sx={{ width: "150px", ml: 1 }}
              />
            </Box>
          </Box>

          <Box className="card-header" display="flex" flexDirection="column" sx={{ mb: "0px !important" }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center">
                <Typography variant="body1" className="interest">Interest:</Typography>
                <EditableField
                  name="account_details.interest"
                  value={formatAmount(formik.values.account_details?.interest)}
                  type="number"
                  placeholder="0.00"
                  editingField={editingField}
                  setEditingField={setEditingField}
                  formik={formik}
                  className="soa-interest-amount"
                  disabled={!isEditable}
                  sx={{ width: "150px", ml: 1 }}
                />
              </Box>
              <Tooltip title="Open Interest Calculator" placement="top">
                <IconButton
                  component={Link}
                  href="https://www.servicealberta.ca/interest-chart.cfm"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "primary.main",
                    "&:hover": {
                      color: "primary.dark",
                      backgroundColor: "rgba(25, 118, 210, 0.04)",
                    },
                  }}
                >
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box className="card-header" display="flex" flexDirection="column">
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center">
                <Typography variant="body1" className="early-move-out-prorate">Emo Pro Rate:</Typography>
                <EditableField
                  name="account_details.early_move_out_prorate"
                  value={formatAmount(formik.values.account_details?.early_move_out_prorate)}
                  type="number"
                  placeholder="0.00"
                  editingField={editingField}
                  setEditingField={setEditingField}
                  formik={formik}
                  className="soa-early-move-out-prorate-amount"
                  disabled={!isEditable}
                  sx={{ width: "150px", ml: 1 }}
                />
              </Box>
              <Tooltip
                title="Please don't add this Amount while calculating the interest for the Security Deposit"
                placement="top"
              >
                <IconButton
                  sx={{
                    color: "primary.main",
                    "&:hover": {
                      color: "primary.dark",
                      backgroundColor: "rgba(25, 118, 210, 0.04)",
                    },
                  }}
                >
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box className="soa-deductions-section" mt={2}>
            <DeductionsTable formik={formik} isEditable={isEditable} />
          </Box>
        </Box>
      </Paper>
    </Box>
  ));

  return (
    <Box className="soa-form-container">
      {showProgress && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <Paper elevation={3} sx={{ p: 4, minWidth: 300, textAlign: 'center' }}>
            <Box className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" sx={{ mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>{progressMessage}</Typography>
            <Box sx={{ width: '100%', mb: 2 }}>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
            <Typography variant="body2" color="text.secondary">{progress}% Complete</Typography>
          </Paper>
        </Box>
      )}
      <Paper elevation={2} className="soa-document-wrapper">
        <div ref={soaFormRef}>
          <Box className="soa-document-header">
            {companyDetails && (
              <>
                {companyDetails.logo && (
                  <img
                    src={companyDetails.logo}
                    alt={`${companyDetails.name} Logo`}
                    className="soa-company-logo"
                    style={{ maxHeight: '50px', maxWidth: '150px', marginBottom: '10px' }}
                  />
                )}
                <Typography variant="h5" className="soa-company-name" sx={{ fontWeight: 'bold', marginBottom: '10px' }}>
                  {companyDetails.name}
                </Typography>
              </>
            )}
            <Typography variant="h4" className="soa-main-title">Landlord's Notice to Tenant</Typography>
            <Typography variant="h3" className="soa-document-title">Security Deposit — Statement of Account</Typography>
            <Typography variant="body2" className="soa-legal-notice">
              This must be forwarded to tenant within 10 days of the tenant giving up possession.
              <br />
              See section 45 and 46 of <em>The Residential Tenancies Act</em>
            </Typography>
          </Box>
          <Box className="soa-info-section">
            <Box className="soa-info-grid">
              <Box className="soa-tenant-name-section">
                <Box className="soa-tenant-row unified-tenant-row">
                  <Typography variant="body1" className="soa-field-label" sx={{ marginRight: '8px' }}>TO:</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: '1rem' }}>
                    {tenants.map((tenant, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: '1rem', width: '100%' }}>
                        <EditableField
                          name={`tenant[${index}].name`}
                          value={tenant.name}
                          placeholder={`Tenant ${index + 1} Name`}
                          editingField={editingField}
                          setEditingField={setEditingField}
                          formik={formik}
                          className="soa-tenant-name"
                          onChange={(value) => handleTenantChange(index, 'name', value)}
                          disabled={!isEditable}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: '8px' }}>
                          <EditableField
                            name={`tenant[${index}].email`}
                            value={tenant.email}
                            placeholder={index === 0 ? 'Tenant 1 Email (Required)' : `Tenant ${index + 1} Email`}
                            editingField={editingField}
                            setEditingField={setEditingField}
                            formik={formik}
                            className="soa-tenant-email"
                            onChange={(value) => handleTenantChange(index, 'email', value)}
                            disabled={!isEditable}
                          />
                          {index > 0 && isEditable && (
                            <Button
                              size="small"
                              onClick={() => setTenantEmailNA(index)}
                              className="soa-na-btn"
                              aria-label={`Set tenant ${index + 1} email to N/A`}
                              sx={{ minWidth: 'auto', padding: '0.25rem', bgcolor: 'grey.200', '&:hover': { bgcolor: 'grey.300' } }}
                            >
                              <FeatherIcon icon="slash" size={14} />
                            </Button>
                          )}
                        </Box>
                        {index > 0 && isEditable && (
                          <Button
                            size="small"
                            onClick={() => removeTenant(index)}
                            className="soa-remove-btn"
                            aria-label={`Remove tenant ${index + 1}`}
                            sx={{ minWidth: 'auto', padding: '0.25rem' }}
                          >
                            <FeatherIcon icon="minus" size={14} />
                          </Button>
                        )}
                        {index === tenants.length - 1 && isEditable && (
                          <Button
                            size="small"
                            onClick={addTenant}
                            className="soa-add-deduction-btn"
                            aria-label="Add tenant"
                            sx={{ minWidth: 'auto', padding: '0.25rem' }}
                          >
                            <FeatherIcon icon="plus" size={14} />
                          </Button>
                        )}
                      </Box>
                    ))}
                    <Typography variant="caption" className="soa-field-subtitle">(Name and Email of Tenant(s))</Typography>
                  </Box>
                </Box>
              </Box>
              <Box className="soa-address-section">
                <Box className="soa-address-row">
                  <Typography variant="body1" className="soa-field-label">AT:</Typography>
                  <Box className="soa-address-column">
                    <EditableField
                      name="property_details.unit_no"
                      value={formik.values?.property_details?.unit_no || ''}
                      placeholder="Unit Number"
                      editingField={editingField}
                      setEditingField={setEditingField}
                      formik={formik}
                      className="soa-address-field"
                      disabled={!isEditable}
                    />
                    <Typography variant="caption" className="soa-field-subtitle">(Unit Number)</Typography>
                  </Box>
                  <Box className="soa-address-column street">
                    <EditableField
                      name="address.street"
                      value={formik.values?.address?.street || ''}
                      placeholder="Street Address"
                      editingField={editingField}
                      setEditingField={setEditingField}
                      formik={formik}
                      className="soa-address-field"
                      disabled={!isEditable}
                    />
                    <Typography variant="caption" className="soa-field-subtitle">(Street Address)</Typography>
                  </Box>
                  <Box className="soa-address-column">
                    <EditableField
                      name="address.city"
                      value={formik.values?.address?.city || ''}
                      placeholder="City"
                      editingField={editingField}
                      setEditingField={setEditingField}
                      formik={formik}
                      className="soa-address-field"
                      disabled={!isEditable}
                    />
                    <Typography variant="caption" className="soa-field-subtitle">(City)</Typography>
                  </Box>
                  <Box className="soa-address-column">
                    <EditableField
                      name="address.province"
                      value={formik.values?.address?.province || ''}
                      placeholder="Province"
                      editingField={editingField}
                      setEditingField={setEditingField}
                      formik={formik}
                      className="soa-address-field"
                      disabled={!isEditable}
                    />
                    <Typography variant="caption" className="soa-field-subtitle">(Province)</Typography>
                  </Box>
                  <Box className="soa-address-column">
                    <EditableField
                      name="address.postal_code"
                      value={formik.values?.address?.postal_code || ''}
                      placeholder="Postal Code"
                      editingField={editingField}
                      setEditingField={setEditingField}
                      formik={formik}
                      className="soa-address-field"
                      disabled={!isEditable}
                    />
                    <Typography variant="caption" className="soa-field-subtitle">(Postal Code)</Typography>
                  </Box>
                  <Box className="soa-address-column">
                    <EditableField
                      name="address.country"
                      value={formik.values?.address?.country || ''}
                      placeholder="Country"
                      editingField={editingField}
                      setEditingField={setEditingField}
                      formik={formik}
                      className="soa-address-field"
                      disabled={!isEditable}
                    />
                    <Typography variant="caption" className="soa-field-subtitle">(Country)</Typography>
                  </Box>
                </Box>
              </Box>
              <Box className="soa-rental-period">
                <Typography variant="h6" className="soa-section-title">Rental Period</Typography>
                <Box className="soa-rental-row">
                  <Typography variant="body1" className="soa-field-label">FROM:</Typography>
                  <Box className="soa-rental-column">
                    <EditableField
                      name="rental_period.start_date"
                      value={formik.values.rental_period?.start_date}
                      type="date"
                      placeholder="Start Date"
                      editingField={editingField}
                      setEditingField={setEditingField}
                      formik={formik}
                      className="soa-rental-field"
                      disabled={!isEditable}
                    />
                    <Typography variant="caption" className="soa-field-subtitle">(Start Date)</Typography>
                  </Box>
                  <Typography variant="body1" className="soa-field-label">TO:</Typography>
                  <Box className="soa-rental-column">
                    <EditableField
                      name="rental_period.end_date"
                      value={formik.values.rental_period?.end_date}
                      type="date"
                      placeholder="End Date"
                      editingField={editingField}
                      setEditingField={setEditingField}
                      formik={formik}
                      className="soa-rental-field"
                      disabled={!isEditable}
                    />
                    <Typography variant="caption" className="soa-field-subtitle">(End Date)</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box className="soa-deposit-section">
            <SecurityDepositSummary />
          </Box>
          <Box className="soa-footer-section">
            <Box className="soa-footer-grid">
              <Box className="soa-footer-left">
                <Typography variant="h6" className="soa-footer-title">Statement Mailed to</Typography>
                <Box>
                  <Box className="soa-editable-field soa-name" sx={{ width: '100%' }}>
                    <Box
                      className="soa-display-value"
                      sx={{
                        width: '100%',
                        border: hasTenantError ? '2px solid' : '2px solid transparent',
                        borderColor: hasTenantError ? 'error.main' : 'transparent',
                        borderRadius: '4px',
                        padding: hasTenantError ? '4px 8px' : '6px 10px',
                        color: isEditable ? 'inherit' : 'inherit',
                      }}
                    >
                      <Typography
                        variant="body2"
                        className="soa-field-text"
                        sx={{ color: isEditable && hasTenantError ? 'error.main' : 'inherit', fontWeight: hasTenantError ? 'medium' : 'normal' }}
                      >
                        {formatTenantsDisplay() || (
                          <span className="soa-placeholder" style={{ color: isEditable && hasTenantError ? 'inherit' : '#999' }}>
                            Name and Email of Tenant(s)
                          </span>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" className="soa-field-subtitle">(Name and Email of Tenant(s))</Typography>
                </Box>
                <Box>
                  <Box className="soa-column">
                    <EditableField
                      name="statement_details.address"
                      value={formik.values.statement_details?.address}
                      placeholder="Address"
                      editingField={editingField}
                      setEditingField={setEditingField}
                      formik={formik}
                      className="soa-name"
                      disabled={!isEditable}
                    />
                    <Typography variant="caption" className="soa-field-subtitle">(Address)</Typography>
                  </Box>
                </Box>
                <Box className="soa-row">
                  <Typography variant="body2" className="soa-field-label mt-2">Date Mailed:</Typography>
                  <Box className="soa-column">
                    <EditableField
                      name="statement_details.mailed_on"
                      value={formik.values.statement_details?.mailed_on}
                      type="date"
                      placeholder="Date Mailed"
                      editingField={editingField}
                      setEditingField={setEditingField}
                      formik={formik}
                      className="soa-name"
                      disabled={!isEditable}
                    />
                  </Box>
                </Box>
              </Box>
              <Box className="soa-footer-right">
                <Typography variant="h6" className="soa-footer-title">Name and Address of Landlord/Agent:</Typography>
                <Box className="soa-row">
                  <Box className="soa-column">
                    <EditableField
                      name="landlord_details.name"
                      value={formik.values.landlord_details?.name}
                      placeholder="Landlord Name"
                      editingField={editingField}
                      setEditingField={setEditingField}
                      formik={formik}
                      className="soa-name"
                      disabled={!isEditable}
                    />
                    <Typography variant="caption" className="soa-field-subtitle">(Name of Landlord/Agent)</Typography>
                  </Box>
                </Box>
                <Box>
                  <Box className="soa-column">
                    <EditableField
                      name="landlord_details.address"
                      value={formik.values.landlord_details?.address}
                      placeholder="Landlord Address"
                      editingField={editingField}
                      setEditingField={setEditingField}
                      formik={formik}
                      className="soa-name"
                      disabled={!isEditable}
                    />
                    <Typography variant="caption" className="soa-field-subtitle">(Address)</Typography>
                  </Box>
                </Box>
                <Box className="soa-row">
                  <Typography variant="body2" className="soa-field-label mt-2">Phone Number:</Typography>
                  <Box className="soa-column">
                    <EditableField
                      name="landlord_details.phone"
                      value={formik.values.landlord_details?.phone}
                      placeholder="Phone Number"
                      editingField={editingField}
                      setEditingField={setEditingField}
                      formik={formik}
                      className="soa-name"
                      disabled={!isEditable}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box className="soa-signature-section">
            <SectionContent>
              {formik.values.landlord_details?.signature ? (
                <img
                  src={formik.values.landlord_details.signature}
                  alt="Landlord Signature"
                  className="soa-signature-field"
                  style={{ maxHeight: '100px', maxWidth: '250px', objectFit: 'contain' }}
                />
              ) : (
                <Typography variant="body2" color="textSecondary">No Signature Available</Typography>
              )}
              <Typography variant="caption" className="soa-signature-subtitle">Signature of Landlord or Landlord's Agent</Typography>
            </SectionContent>
          </Box>
        </div>
        {!isEditable && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, my: 4 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadPDF}
              disabled={showProgress}
              aria-label="Download SOA as PDF"
              sx={{ minWidth: '150px' }}
            >
              Download PDF
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export { SOAForm, DeductionsTable };